import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { GlobalConstants } from 'src/app/constant/constant';
import { environment } from 'src/environments/environment';
import { Md5 } from 'ts-md5';
import { OfflinePasswordService } from '../offline-password.service';
import { AuthSessionService } from './auth-session.service';

/** Authenticates with the server and coordinates login UI side effects; session state and persistence belong to AuthSessionService. */

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  readonly ready: Promise<void>;
  private readonly sessionSubscription: Subscription;
  get timeoutInterval(): ReturnType<typeof setTimeout> | undefined { return this.sessions.timeoutInterval; }

  constructor(
    protected http: HttpClient, 
    private router: Router, 
    private logService: LogService,
    private offlinePassword: OfflinePasswordService,
    @Inject(AuthSessionService) private sessions: AuthSessionService,
  ) {
    this.ready = this.sessions.ready;
    this.sessionSubscription = this.sessions.state$.subscribe(state => {
      if (state.phase === 'anonymous') {
        if (state.reason === 'expired' || state.reason === 'invalid') {
          this.router.navigate(['auth/login'], { replaceUrl: true });
          this.logService.event('Auth.Logout');
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.sessionSubscription.unsubscribe();
  }

  clearSession(): Promise<boolean> { return this.sessions.end(); }

  login(email: string, password: string, force: boolean) {
    const uri = environment.authApiUrl + '/Auth/login';
    const md5Password = Md5.hashStr(password);

    return this.http
      .post<any>(uri, {
        email,
        password: md5Password,
        apiKey: environment.authApiKey,
        force,
      })
      .pipe(
        concatMap(async (user) => {
          if (user && user.AccessToken) {
            const identity = await this.sessions.signIn(user);
            if (!identity) { throw new Error('Authentication session was superseded'); }

            try {
              await this.offlinePassword.hashAndStore(password, record => this.sessions.storeOfflinePassword(identity, record));
            } catch (e) {
              this.logService.error('Auth.OfflinePasswordStoreFailed', e as any);
            }

            if (this.sessions.completeSignIn(identity)) {
              this.logService.event('Auth.LoginSuccess', { expiresAt: this.sessions.state.expiresAt });
            }
          }
          return user;
        })
      );
  }

  async logout(): Promise<boolean> {
    const sharedCleanupSucceeded = await this.clearSession();
    if (this.sessions.state.phase !== 'anonymous') { return false; }
    this.router.navigate(['auth/login'], { replaceUrl: true });
    this.logService.event('Auth.Logout', { SharedCleanupSucceeded: sharedCleanupSucceeded });
    return sharedCleanupSucceeded;
  }

  public isAuthenticated(): boolean {
    return this.sessions.isAuthenticated();
  }

  getRememberEmailStatus(): boolean {
    try {
      return JSON.parse(localStorage.getItem(GlobalConstants.REMEMBER_EMAIL));
    } catch {
      return false;
    }
  }

  setRememberEmailStatus(status: boolean) {
    localStorage.setItem(GlobalConstants.REMEMBER_EMAIL, JSON.stringify(status));
  }

  /**
   * Attempt offline password verification against the locally stored slow hash.
   * Returns true if the stored hash exists and matches the provided password.
   * Intended for features like re-auth gating (e.g., unlocking sensitive cached data while offline).
   */
  async verifyOfflinePassword(password: string): Promise<boolean> {
    const identity = this.sessions.state.identity;
    if (!identity || !this.sessions.isAuthenticated()) { return false; }
    const verified = await this.offlinePassword.verify(password);
    return verified && this.sessions.isAuthenticated() && this.sessions.isCurrent(identity);
  }
}
